<template>
  <div class="date-range-selector">
    <load :loaded="loading" name="date" :information="true">
      <date-picker
        v-model:value="dateTime"
        range
        format="DD/MMMM/YYYY"
        range-separator=" a "
        placeholder="Selecionar Período"
        :disabled-date="(date) => date >= new Date()"
        :clearable="false"
        @pick="pickingData"
      >
        <template v-slot:sidebar="{ emit }">
          <div style="text-align: left">
            <p>Selecione um período</p>

            <button
              v-for="(period, index) in datePeriodOptions"
              :key="index"
              class="mx-btn mx-btn-text d-flex align-items-center"
              @click="selectPeriod(emit, period.numberOfDays)"
            >
              <i class="icon-nav-bullet_right arrow-date-picker" />
              {{ period.label }}
            </button>
          </div>
        </template>
        <template v-slot:icon-calendar>
          <i class="icon-nav-bullet_down date-range-selector__icon" />
        </template>
        <template v-slot:input="data">
          <span class="date-range-selector__label d-block">
            {{ getLabel(data.value) }}
          </span>
        </template>
      </date-picker>
    </load>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import DatePicker from "vue-datepicker-next";
import "vue-datepicker-next/index.css";
import "vue-datepicker-next/locale/pt-br";

export default defineComponent({
  name: "DateRange",

  components: {
    DatePicker,
  },

  props: {
    time: { type: Array, required: true },
    currentDay: { type: Boolean, default: false },
  },

  data() {
    return {
      dateTime: [] as any,
      label: "",
      datePeriodOptions: [
        {
          label: "Últimos 7 dias",
          numberOfDays: 7,
        },
        {
          label: "Últimos 15 dias",
          numberOfDays: 15,
        },
        {
          label: "Últimos 30 dias",
          numberOfDays: 30,
        },
      ],
    };
  },

  created() {
    this.dateTime = this.time;
  },

  mounted() {
    this.label = "últimos 7 dias";
  },

  watch: {
    dateTime(value) {
      this.$emit("dateUpdated", value);
    },
  },

  computed: {
    ...mapGetters("loading", ["loading"]),
  },

  methods: {
    selectPeriod(emit: any, num: number) {
      switch (num) {
        case 7:
          this.label = "últimos 7 dias";
          break;
        case 15:
          this.label = "últimos 15 dias";
          break;
        case 30:
          this.label = "últimos 30 dias";
          break;
      }

      const start = new Date();
      const end = new Date();

      end.setTime(end.getTime() - num * 24 * 3600 * 1000);
      start.setTime(
        this.currentDay
          ? start.getTime() - 3600 * 1000
          : start.getTime() - 24 * 3600 * 1000
      );

      const date = [end, start];

      emit(date);
    },

    getLabel(value: string) {
      return this.label ? this.label : value.replace("~", "a");
    },

    pickingData() {
      this.label = "";
    },
  },
});
</script>

<style lang="scss">
.date-range-selector {
  cursor: pointer;

  &__label {
    color: var(--insights-color-16);
    font-size: toRem(16px);
    padding-right: 26px;
  }

  &__icon {
    color: var(--insights-color-16);
    margin-left: 10px;
    cursor: pointer;
  }

  .mx {
    &-input-wrapper {
      > i {
        > i {
          color: var(--insights-color-16);
          font-size: toRem(11px);
          cursor: pointer;
        }
      }
    }
  }
}

// Calendário ao ser renderizado é feito ao mesmo nível
// do <div id="app"></div>
// Por isso as regras são declaradas fora

.mx-datepicker-main {
  border: 1px solid #f2f0f0;
  border-radius: 8px;
  padding: 20px;

  .mx-datepicker-sidebar {
    width: 180px;
    // padding: 20px 25px;
    p {
      font-size: toRem(13px);
      line-height: toRem(15px);
      color: #c9c9c9;
      font-family: "title-tertiary";
      text-transform: uppercase;
      margin-bottom: 30px;
    }

    .mx-btn {
      font-family: "text-semi-bold";
      font-size: toRem(15px);
      line-height: toRem(40px);
      color: var(--insights-color-16);

      > i {
        font-size: toRem(10px);
        color: var(--insights-color-16);
        margin-right: 6px;
      }

      &:hover {
        color: var(--insights-color-1);
      }
    }
  }
}

.mx-datepicker-range {
  width: fit-content;
}

.mx-datepicker-sidebar + .mx-datepicker-content {
  margin-left: 180px;
  border: none;
}
.mx-calendar {
  margin: 0 15px;
}
.mx-calendar.mx-calendar-panel-date {
  border: none;
}

.mx-table-date td {
  color: #787473;
}

.mx-calendar-header-label .mx-btn {
  color: #787473;
  font-size: toRem(16px);
  line-height: toRem(19px);
  font-family: "title-tertiary";
}
.cell + .cell.active {
  /* first-active-cell */
  border-bottom-left-radius: 6px;
  border-top-left-radius: 6px;
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
  background-color: var(--insights-color-1);
}
.cell.in-range ~ .cell.active {
  /* last-active-cell */
  border-bottom-left-radius: 0;
  border-top-left-radius: 0;
  border-bottom-right-radius: 6px;
  border-top-right-radius: 6px;
}
.cell + .today {
  color: var(--insights-color-1);
}
.cell.active ~ .cell.active {
  /* last-active-cell */
  border-bottom-left-radius: 0;
  border-top-left-radius: 0;
  border-bottom-right-radius: 6px;
  border-top-right-radius: 6px;
}
[data-year].cell.active,
[data-month].cell.active {
  border-radius: 6px;
}
.mx-calendar-content .cell {
  font-size: toRem(15px);
}
.mx-calendar-content .cell.in-range,
.mx-calendar-content .cell.hover-in-range,
.mx-calendar-content .cell:hover {
  background-color: #f2f0f0;
}
.mx-calendar-content .cell.in-range,
.mx-calendar-content .cell.hover-in-range {
  color: #787473;
  // font-weight: 600;
}
.mx-calendar-content .cell.active {
  // font-weight: 600;
}
</style>
